import React from 'react';
import { AlertCircle } from 'lucide-react';

/**
 * Possible error formats from the API or other sources
 */
type ErrorType =
  | string
  | { message: string }
  | { error: string }
  | Array<{
      kind?: string;
      type?: string;
      message?: string;
      path?: Array<{ key?: string; [key: string]: unknown }>;
      [key: string]: unknown;
    }>
  | { error: ErrorType} | unknown;

interface ErrorMessageProps {
  error: ErrorType;
  className?: string;
  showIcon?: boolean;
}

/**
 * Generic error message component that handles multiple error formats
 * 
 * Supported formats:
 * - string: "Error message"
 * - { message: "..." }
 * - { error: "..." }
 * - Array of validation errors with message field
 * - Any other format will show "An unknown error occurred"
 */
const ErrorMessage: React.FC<ErrorMessageProps> = ({ 
  error, 
  className = '',
  showIcon = true 
}) => {
  if (!error) return null;

  /**
   * Extract human-readable error message(s) from various error formats
   */
  const extractErrorMessages = (err: ErrorType): string[] => {
    // Handle null/undefined
    if (!err) {
      return [];
    }

    while(err && typeof err === 'object' && 'error' in err) {
      err = err.error;
    }

    // Handle string
    if (typeof err === 'string') {
      if(err.startsWith('<!DOCTYPE html>')) {
        return ["Le serveur a renvoyé une erreur inconnue"];
      }

      return err.trim() ? [err] : [];
    }

    // Handle array of validation errors
    if (Array.isArray(err)) {
      const messages = err
        .map((item) => {
          if (typeof item === 'string') return item;
          if (item && typeof item === 'object') {
            // Try to extract message
            if ('message' in item && typeof item.message === 'string') {
              return item.message;
            }
            // Try to build a message from validation error
            if ('path' in item && Array.isArray(item.path) && item.path.length > 0) {
              const field = item.path[item.path.length - 1]?.key || 'field';
              const message = item.message || `Validation error on ${field}`;
              return message;
            }
          }
          return null;
        })
        .filter((msg): msg is string => msg !== null && msg.trim() !== '');
      
      return messages.length > 0 ? messages : ['Validation error occurred'];
    }

    // Handle object with message or error field
    if (typeof err === 'object' && err !== null) {
      // Try { message: "..." }
      if ('message' in err && typeof err.message === 'string' && err.message.trim()) {
        return [err.message];
      }
      
      // Try { error: "..." }
      if ('error' in err && typeof err.error === 'string' && err.error.trim()) {
        return [err.error];
      }

      // Try to extract any string property
      const stringValues = Object.values(err)
        .filter((val): val is string => typeof val === 'string' && val.trim() !== '');
      
      if (stringValues.length > 0) {
        return stringValues;
      }
    }

    // Fallback for unknown formats
    return ['An unknown error occurred'];
  };

  const messages = extractErrorMessages(error);

  if (messages.length === 0) {
    return null;
  }

  return (
    <div 
      className={`p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm ${className}`}
      role="alert"
    >
      <div className="flex items-start gap-2">
        {showIcon && (
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
        )}
        <div className="flex-1">
          {messages.length === 1 ? (
            <p>{messages[0]}</p>
          ) : (
            <ul className="list-disc list-inside space-y-1">
              {messages.map((msg, index) => (
                <li key={index}>{msg}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default ErrorMessage;

